import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage';
import { BehaviorSubject, Subject, Observable } from 'rxjs';
import { StatoCompletoRicambio, Ricambio } from 'src/app/common/models/Ricambio';

@Injectable({
  providedIn: 'root'
})
export class RicambiDaSincronizzareService {
  private readonly STORAGE_KEY_RICAMBI_DA_SINCRONIZZARE_PREFISSO = '__ricambiDaSincronizzare';

  /**
   * Per ogni intervento, identificato dal proprio id, mantiene un Subject che propaga
   * ai sottoscriventi l'elenco degli id dei suoi ricambi disallineati
   */
  private mapIdRicambiDisallineatiSubject: Map<number, Subject<string[]>> = new Map<number, Subject<string[]>>();

  constructor(private storage: Storage) { }

  private getRicambioKey(idProdotto: number, codiceMagazzinoSelezionato?: string): string {
    return `${idProdotto}__${(codiceMagazzinoSelezionato || '').trim()}`;
  }

  private getDepositoKey(codiceMagazzinoSelezionato?: string): string {
    return (codiceMagazzinoSelezionato || '').trim();
  }

  async getElencoIdRicambiDisallineatiObservable(idIntervento: number): Promise<Observable<string[]>> {
    const ricambiInterventoObs = this.getRicambiInterventoSubj(idIntervento);
    await this.aggiornaObservableIdRicambiDisallineati(idIntervento);
    return ricambiInterventoObs.asObservable();
  }

  private getRicambiInterventoSubj(idIntervento: number): Subject<string[]> {
    let subj = this.mapIdRicambiDisallineatiSubject.get(idIntervento);
    if (!subj) {
      subj = new BehaviorSubject<string[]>([]);
      this.mapIdRicambiDisallineatiSubject.set(idIntervento, subj);
    }
    return subj;
  }

  async salvaRicambioDaSincronizzare(statoRicambio: StatoCompletoRicambio): Promise<StatoCompletoRicambio[]> {
    const ricambiFiltrati = await this.rimuoviRicambioDaSincronizzare(statoRicambio);
    ricambiFiltrati.unshift(statoRicambio);
    await this.storage.set(this.getStorageKey(statoRicambio.datiRicambio.idIntervento), ricambiFiltrati);
    return ricambiFiltrati;
  }

  async rimuoviRicambioDaSincronizzare(statoRicambioDaRimuovere: StatoCompletoRicambio): Promise<StatoCompletoRicambio[]> {
    const { idIntervento, idProdotto, codiceMagazzinoSelezionato } = statoRicambioDaRimuovere.datiRicambio;
    const depositoDaRimuovere = this.getDepositoKey(codiceMagazzinoSelezionato);

    const statoPrecedenteRicambi = await this.getRicambiDaSincronizzare(idIntervento);

    const ricambiFiltrati = statoPrecedenteRicambi.filter(statoRicambio => {
      const stessoProdotto = statoRicambio.datiRicambio.idProdotto === idProdotto;
      const stessoDeposito =
        this.getDepositoKey(statoRicambio.datiRicambio.codiceMagazzinoSelezionato) === depositoDaRimuovere;

      return !(stessoProdotto && stessoDeposito);
    });

    await this.storage.set(this.getStorageKey(idIntervento), ricambiFiltrati);
    return ricambiFiltrati;
  }

  async rimuoviRicambiDaSincronizzare(idIntervento: number) {
    const key = this.getStorageKey(idIntervento);
    await this.storage.remove(key);
  }

  async isModificheDaSincronizzarePresenti(ricambio: Ricambio): Promise<boolean> {
    const { idIntervento, idProdotto, codiceMagazzinoSelezionato } = ricambio;
    return !!await this.getRicambioDaSincronizzare(idIntervento, idProdotto, codiceMagazzinoSelezionato);
  }

  async isRicambioPresenteSoloOffline(
    idIntervento: number,
    idProdotto: number,
    codiceMagazzinoSelezionato?: string
  ): Promise<boolean> {
    const depositoCercato = this.getDepositoKey(codiceMagazzinoSelezionato);
    const ricambiSoloOffline = await this.getRicambiPresentiSoloOffline(idIntervento);

    return ricambiSoloOffline.some(statoRicambio =>
      statoRicambio.datiRicambio.idProdotto === idProdotto &&
      this.getDepositoKey(statoRicambio.datiRicambio.codiceMagazzinoSelezionato) === depositoCercato
    );
  }

  async getRicambiPresentiSoloOffline(idIntervento: number): Promise<StatoCompletoRicambio[]> {
    const ricambiDisallineati = await this.getRicambiDaSincronizzare(idIntervento);
    return ricambiDisallineati
      .filter(statoRicambio => statoRicambio.isPresenteSoloLocalmente === true);
  }

  async getRicambioDaSincronizzare(
    idIntervento: number,
    idRicambioCercato: number,
    codiceMagazzinoSelezionato?: string
  ): Promise<StatoCompletoRicambio> {
    const depositoCercato = this.getDepositoKey(codiceMagazzinoSelezionato);
    const ricambi = await this.getRicambiDaSincronizzare(idIntervento);

    return ricambi.find(statoRicambio =>
      statoRicambio.datiRicambio.idProdotto === idRicambioCercato &&
      this.getDepositoKey(statoRicambio.datiRicambio.codiceMagazzinoSelezionato) === depositoCercato
    );
  }

  private async aggiornaObservableIdRicambiDisallineati(idIntervento: number): Promise<string[]> {
    const ricambiDaSincronizzare = await this.getRicambiDaSincronizzare(idIntervento);
    const elencoId = ricambiDaSincronizzare.map(statoRicambio =>
      this.getRicambioKey(
        statoRicambio.datiRicambio.idProdotto,
        statoRicambio.datiRicambio.codiceMagazzinoSelezionato
      )
    );
    const ricambiSubj = this.getRicambiInterventoSubj(idIntervento);
    ricambiSubj.next(elencoId);
    return elencoId;
  }

  async getRicambiDaSincronizzare(idIntervento: number): Promise<StatoCompletoRicambio[]> {
    const storageKey = this.getStorageKey(idIntervento);
    const ricambiSuStorage = await this.storage.get(storageKey);
    const ricambiDaSincronizzare = !!ricambiSuStorage ? ricambiSuStorage : [];
    return ricambiDaSincronizzare;
  }

  private getStorageKey(idIntervento: number): string {
    return `${this.STORAGE_KEY_RICAMBI_DA_SINCRONIZZARE_PREFISSO}_${idIntervento}`;
  }
}
